/**
 * An image a reply made, in a box of its final shape from the start.
 *
 * While it is being made the box holds a heavily blurred wash that drifts,
 * with a spinner — the model gives no preview, so this stands in for one.
 * When the file arrives it comes up from the blur to sharp; an image from
 * earlier in the chat just fades in. Click opens the full file in a new tab,
 * the same as a sent attachment.
 */

import { useRef, useState } from 'react';
import { fileUrl } from '../../api';
import { Ph } from '../ui';

/** The widest an image is drawn: wider for a wide one, so it is not tiny. */
const maxWidth = (width: number, height: number) => (width > height * 1.2 ? 560 : width < height ? 360 : 420);

/** How long after it was made an image still arrives with the reveal. */
const FRESH_SECONDS = 120;

/** Made in the last two minutes: `createdAt` is in seconds. */
export const isFresh = (createdAt: number): boolean => Date.now() / 1000 - createdAt < FRESH_SECONDS;

export function GeneratedImageView({
  fileId,
  prompt,
  width,
  height,
  failed = false,
  fresh = false
}: {
  /** Null while it is being made. */
  fileId: string | null;
  prompt: string;
  width?: number;
  height?: number;
  failed?: boolean;
  /** Just made: comes up from the blur, even if this view never saw it being
   *  made (a `/image` reply's card takes over from the live row). */
  fresh?: boolean;
}) {
  const [loaded, setLoaded] = useState(false);
  const sawMaking = useRef(!fileId);
  const reveal = fresh || sawMaking.current;
  const w = width ?? 1024;
  const h = height ?? 1024;
  const making = !failed && !fileId;
  const wash = !failed && reveal && !loaded;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start', width: '100%' }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: maxWidth(w, h),
          aspectRatio: `${w} / ${h}`,
          borderRadius: 'var(--radius-app-card)',
          overflow: 'hidden',
          background: 'var(--app-panel-sunken)'
        }}
      >
        {wash ? <div aria-hidden="true" className="gen-image-wash" style={{ position: 'absolute', inset: '-20%' }} /> : null}
        {fileId ? (
          <a
            href={fileUrl(fileId)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ position: 'absolute', inset: 0, display: 'block' }}
          >
            <img
              src={fileUrl(fileId)}
              alt={prompt}
              onLoad={() => setLoaded(true)}
              className={loaded ? (reveal ? 'gen-image-reveal' : 'fade-in') : undefined}
              style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', opacity: loaded ? 1 : 0 }}
            />
          </a>
        ) : null}
        {making ? (
          <div
            role="status"
            style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--text-muted)' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <Ph name="circle-notch" size={16} className="spin" />
              Creating image
            </span>
          </div>
        ) : null}
        {failed ? (
          <div
            role="status"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              padding: 16,
              textAlign: 'center',
              fontSize: 13,
              color: 'var(--text-muted)'
            }}
          >
            The image could not be made.
          </div>
        ) : null}
      </div>
      {prompt ? <span style={{ fontSize: 12.5, color: 'var(--text-faint)', maxWidth: maxWidth(w, h) }}>{prompt}</span> : null}
    </div>
  );
}
